import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  FormGroupDirective,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  CallabilityEnum,
  CouponEnum,
  ParticipationBaseBarrierEnum,
  ParticipationBaseEnum,
  ParticipationDownsideEnum,
  ParticipationEnum,
  PayoffEnum,
  ProductCaptureGroupsEnum,
  ProductTypeEnum,
  SummaryEnum,
} from '../../model/enums/product-capture.enums';

import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import {
  EMPTY,
  OperatorFunction,
  Subject,
  Subscription,
  combineLatest,
  of,
} from 'rxjs';
import {
  debounceTime,
  first,
  map,
  pairwise,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { PRODUCT_CALL_EVENT_TYPES } from 'src/app/form-config/mockups/consts/product-call-event-type.const';
import {
  PAYOFF_PARTICIPATION_CAPITAL_BASE_TYPE,
  PAYOFF_PARTICIPATION_DOWNSIDE_TYPE,
} from 'src/app/form-config/mockups/consts/product-capture-field-selectors.consts';
import { PRODUCT_COUPON_TYPES } from 'src/app/form-config/mockups/consts/product-coupon-type.const';
import { DcInputNumberPercentIndicativeFinalComponent } from 'src/app/ui-kit/dc-input-number-percent-indicative-final/dc-input-number-percent-indicative-final.component';
import { FormConfigService } from '../../../form-config/form-config.service';
import { Records } from '../../../form-config/model/records.type';
import {
  BARRIER_AMOUNT,
  BARRIER_AMOUNT_WITH_CUSTOM,
} from '../../model/consts/barrier-amout.const';
import { CAPITAL_BASE_TYPE } from '../../model/consts/capital-base-type.const';
import { DAY_COUNT } from '../../model/consts/day-count.const';
import { DWMY_RANGES } from '../../model/consts/dwmy-ranges.const';
import { FREQUENCY } from '../../model/consts/frequency.const';
import { LEVERAGE_OPTIONS } from '../../model/consts/leverage-options.const';
import { OBS_FREQUENCY } from '../../model/consts/obvs-frequency.const';
import {
  PARTICIPATION_BASE_TYPE,
  PRODUCT_BASE_TYPES,
} from '../../model/consts/participation-base-type.const';
import {
  PARTICIPATION_DOWNSIDE_TYPE,
  PRODUCT_DOWNSIDE_TYPES,
} from '../../model/consts/participation-downside-type.const';
import { PERFORMANCE_CALCULATION_TYPES } from '../../model/consts/performance-calculation.const';
import { RATE_INTERPOLATION } from '../../model/consts/rate-interpolation.const';
import { RateOptions } from '../../model/consts/rate-reset-fields.consts';
import { RATE_RESET } from '../../model/consts/rate-reset.const';
import {
  PRODUCT_UPSIDE_TYPES,
  UPSIDE_TYPE_ITEMS,
} from '../../model/consts/upside-type.const';
import { YES_NO } from '../../model/consts/yes-no.const';
import { DropDownOption } from '../../model/interfaces/drop-down-option.interface';
import { PayoffSchedule } from '../../model/interfaces/payoff-schedule.interface';
import { CalendarType } from '../../model/interfaces/trading-calendar.interface';
import { ProductCaptureService } from '../../services/product-capture.service';
import { DateObjectToDDMMYYYstring } from '../../utils/convert-date-object-to-dd-mm-yyyy-string';
import { getFinalOrIndicativeValueFromIndicativeObject } from '../../utils/get-value-from-indicative-object';
import { CommonProductCaptureDataService } from '../common-product-capture-data.service';
import { capVsDiscountCertificateValidator } from './validators/discount-certificate.validators';
import { BARRIER_BREAK } from '../../model/consts/barrier-break.const';
import { COUPON_CALCULATION_DAYS } from '../../model/consts/coupon-calculation-days.const';
import { INVESTMENT_LEVELS } from '../../model/consts/investment-level.const';

@Component({
  selector: 'app-payoff',
  templateUrl: './payoff.component.html',
  styleUrls: ['./payoff.component.scss'],
})
export class PayoffComponent implements OnInit, OnDestroy {
  @Input() formGroupName: string;
  @Input() formName: string;
  @Input() productId: string;
  @Output() dateRangeOut = new EventEmitter();
  @Input() canUserEdit = true;

  @ViewChild('strikeInputRef', {
    read: DcInputNumberPercentIndicativeFinalComponent,
  })
  strikeInputRef: DcInputNumberPercentIndicativeFinalComponent;

  allCallEventTypeItems: Records = [];
  callEventTypeItems: Records = [];

  couponTypeItems: Records = [];
  allCouponTypeItems: Records = [];

  downSidetypeItems: Records;
  labels: Record<string, any>;
  referenceRateItems: Records = [];

  form: UntypedFormGroup;
  baseTypeItems: DropDownOption[] = PARTICIPATION_BASE_TYPE;
  dayCountItems: DropDownOption[] = DAY_COUNT;
  couponCalculationDaysItems: DropDownOption[] = COUPON_CALCULATION_DAYS;
  frequencyItems: DropDownOption[] = FREQUENCY;
  barrierBreakItems: DropDownOption[] = BARRIER_BREAK;
  rateResetItems: DropDownOption[] = RATE_RESET;

  rateInterpolationItems: DropDownOption[] = RATE_INTERPOLATION;
  upsideTypeItems: DropDownOption[] = UPSIDE_TYPE_ITEMS;
  capitalBaseTypeItems: DropDownOption[] = CAPITAL_BASE_TYPE;

  BARRIER_AMOUNT = BARRIER_AMOUNT;
  BARRIER_AMOUNT_WITH_CUSTOM = BARRIER_AMOUNT_WITH_CUSTOM;
  OBS_FREQUENCY = OBS_FREQUENCY;
  BASE_BARRIER_OBS_FREQUENCY = OBS_FREQUENCY;
  rebateSettlementLagItems = DWMY_RANGES;
  YES_NO = YES_NO;
  PERFORMANCE_CALCULATION_TYPES = PERFORMANCE_CALCULATION_TYPES;
  LEVERAGE_OPTIONS = LEVERAGE_OPTIONS;
  INVESTMENT_LEVELS = INVESTMENT_LEVELS;
  DROP_BACK_MIN_BARRIER_LEVELS = 1;
  DROP_BACK_DEFAULT_BARRIER_LEVELS = 3;

  rateCalculationMethodItems = RateOptions.CALCULATION_METHOD;
  rateBusinessDaysOptions = RateOptions.BUSINESS_DAYS;
  rateSuspensionPeriodsOptions = RateOptions.SUSPENSION_PERIODS;

  destroy$ = new Subject<boolean>();

  discountCertificateSubscriptions = new Subscription();

  settlementCalendars: DropDownOption[];

  constructor(
    private formConfigService: FormConfigService,
    public rootFormGroup: FormGroupDirective,
    private commonProductCaptureDataService: CommonProductCaptureDataService,
    private productCaptureService: ProductCaptureService
  ) {}

  ngOnInit(): void {
    this.labels = this.formConfigService.getLabels();
    this.getDropdownOptions();
    this.form = this.rootFormGroup.control.get(
      this.formGroupName
    ) as UntypedFormGroup;
    this.allCallEventTypeItems = this.formConfigService.getOptionsByFieldName(
      CallabilityEnum.callEventType,
      this.formName
    );

    this.allCouponTypeItems = this.formConfigService
      .getOptionsByFieldName(CouponEnum.couponType, this.formName)
      ?.sort((a, b) => {
        // Bring 'NONE' to the top, otherwise sort alphabetically
        if (a.id === 'NONE') {
          return -1;
        }
        if (b.id === 'NONE') {
          return 1;
        }

        return a.displayValue.localeCompare(b.displayValue);
      });

    if (this.productId === 'new') {
      this.couponTypeItems = this.allCouponTypeItems;
      this.callEventTypeItems = this.allCallEventTypeItems;
    }

    this.downSidetypeItems = this.formConfigService.getOptionsByFieldName(
      ParticipationDownsideEnum.downsideType,
      this.formName
    );

    this.productType.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(200))
      .subscribe(() => this.updateProductDependentFields());

    const summary = this.rootFormGroup.control.get('summary');
    summary
      .get('frequency')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.updateFrequencyFieldIfNecessary('coupon', value);
        this.updateFrequencyFieldIfNecessary('callability', value);
      });

    this.downside
      .get('downsideType')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.productType.value === ProductTypeEnum.DUAL_CURRENCY_NOTE) {
          this.downside.get('floorStrike').disable();
        }

        if (
          this.productType.value === ProductTypeEnum.DUAL_CURRENCY_NOTE &&
          this.downsideStrikeValue?.value?.includes('%')
        ) {
          this.downside.get('leverage')['readOnly'] = true;
        }
      });

    this.setupBonusAmountSubscription();

    this.base
      .get('strike')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.updateBaseLeverage(value));

    this.downside
      .get('strike')
      .valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe((strikeValue) => {
        const strike = strikeValue?.value || '100%';
        this.updateLeverageOptions(strike);
      });

    this.rateObsPeriodShift.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.rateObsPeriodShiftExtraCalendars[
          value !== '0B' ? 'enable' : 'disable'
        ]();
      });
  }

  updateFrequencyFieldIfNecessary(form: string, value: any): void {
    const frequencyField = this.form.get(form).get('frequency');
    if (
      !frequencyField.value ||
      (!frequencyField.touched && this.productId === 'new')
    ) {
      frequencyField.setValue(value);
    }
  }

  updateProductDependentFields(): void {
    this.updateCouponTypeOptions();
    this.updateUpsideTypeOptions();
    this.updateDownsideTypeOptions();
    this.updateBaseTypeOptions();
    this.updateCallEventTypeOptions();

    this.downSidetypeItems =
      this.formConfigService.getOptionsForFieldByProductType(
        PAYOFF_PARTICIPATION_DOWNSIDE_TYPE,
        this.productType.value,
        this.formName
      );

    this.capitalBaseTypeItems =
      this.formConfigService.getOptionsForFieldByProductType(
        PAYOFF_PARTICIPATION_CAPITAL_BASE_TYPE,
        this.productType.value,
        this.formName
      );

    this.setDefaultsForBonusCertificate();
    this.setDefaultsForDropBackCertificate();
    this.handleDiscountCertificate();
  }

  updateCouponTypeOptions(): void {
    const couponTypesByProduct = PRODUCT_COUPON_TYPES.get(
      this.productType.value
    );

    if (!couponTypesByProduct) {
      this.couponTypeItems = this.allCouponTypeItems;
    } else {
      this.couponTypeItems = this.allCouponTypeItems.filter(
        (record: DropDownOption) => couponTypesByProduct.includes(record.id)
      );
    }
  }

  updateUpsideTypeOptions(): void {
    if (!!this.productType.value) {
      this.upsideTypeItems = PRODUCT_UPSIDE_TYPES.get(this.productType.value);
    }
  }

  updateDownsideTypeOptions(): void {
    if (!!this.productType.value) {
      this.downSidetypeItems =
        PRODUCT_DOWNSIDE_TYPES.get(this.productType.value) ??
        PARTICIPATION_DOWNSIDE_TYPE;
    }
  }

  updateBaseTypeOptions(): void {
    if (!!this.productType.value) {
      this.baseTypeItems =
        PRODUCT_BASE_TYPES.get(this.productType.value) ??
        PARTICIPATION_BASE_TYPE;
    }
  }

  updateCallEventTypeOptions(): void {
    const callEventTypesByProduct = PRODUCT_CALL_EVENT_TYPES.get(
      this.productType.value
    );

    if (!callEventTypesByProduct) {
      this.callEventTypeItems = this.allCallEventTypeItems;
    } else {
      this.callEventTypeItems = this.allCallEventTypeItems.filter(
        (record: DropDownOption) => callEventTypesByProduct.includes(record.id)
      );
    }
  }

  calculateLeverage(strike: string): string | null {
    if (strike && strike.includes('%')) {
      const numericStrike = parseFloat(strike.replace(/%/g, ''));
      return numericStrike ? (1 / (numericStrike / 100)).toString() : '1';
    }

    return null;
  }

  updateLeverageOptions(strike: string): void {
    const leverageControl = this.downside.get('leverage');
    const leverageOptionsControl = this.downside.get('leverageOptions');
    const calculatedLeverage = this.calculateLeverage(strike);

    if (
      calculatedLeverage !== undefined &&
      calculatedLeverage !== '1' &&
      calculatedLeverage !== null
    ) {
      leverageControl.setValue({
        minValue: null,
        maxValue: null,
        value: calculatedLeverage,
        indicativeValue: null,
      });

      leverageOptionsControl.setValue(true);
    } else {
      leverageControl.setValue({
        minValue: null,
        maxValue: null,
        value: '1',
        indicativeValue: null,
      });

      leverageOptionsControl.setValue(false);
    }
  }

  private setDefaultsForBonusCertificate(): void {
    if (this.productType.value !== 'BONUS_CERTIFICATE') {
      return;
    }

    if (this.productId !== 'new') {
      this.base.get('capped').setValue(!!this.base.get('globalCap').value);
      return;
    }

    this.base.get(ParticipationBaseEnum.strike).setValue({
      value: '100%',
      minValue: null,
      indicativeValue: null,
      maxValue: null,
    });
    this.base.get(ParticipationBaseEnum.upperParticipation).setValue({
      value: '100%',
      minValue: null,
      indicativeValue: null,
      maxValue: null,
    });
    this.baseBarrier
      .get(ParticipationBaseBarrierEnum.obsFrequency)
      .setValue('PERIOD_END');
    this.baseBarrier
      .get(ParticipationBaseBarrierEnum.touchAndBreak)
      .setValue(false);
    this.base.get('capped').setValue(false);
  }

  private setDefaultsForDropBackCertificate(): void {
    if (this.productType.value !== ProductTypeEnum.DROP_BACK_CERTIFICATE) {
      this.baseBarrierLevelsAndInvestments?.clear();
      this.BASE_BARRIER_OBS_FREQUENCY = OBS_FREQUENCY;

      return;
    }

    this.BASE_BARRIER_OBS_FREQUENCY = OBS_FREQUENCY.filter(
      (item) => item.id !== 'PERIOD_END'
    );

    if (this.productId !== 'new') {
      return;
    }

    for (let i = 0; i < this.DROP_BACK_DEFAULT_BARRIER_LEVELS; i++) {
      this.addBarrierAndInvestmentLevel();
    }
  }

  handleDiscountCertificate(): void {
    this.discountCertificateSubscriptions.unsubscribe();
    this.toggleDiscountCertificateFields();
    this.observeDiscountCertificateFields();
    if (this.productId !== 'new') {
      const globalCapExists = this.base.get('globalCap').value?.value;
      this.base.get('capped').setValue(!!globalCapExists);
      return;
    }
  }

  observeDiscountCertificateFields(): void {
    this.discountPriceField.valueChanges
      .pipe(
        this.discountCertificateOrEmpty(),
        startWith(this.discountPriceField.value),
        takeUntil(this.destroy$),
        debounceTime(100),
        map(() => this.strikeInputRef?.unit),
        pairwise()
      )
      .subscribe(([prev, current]) => {
        if (prev !== current) {
          this.toggleIssueAtDiscount();
        }

        if (this.issueAtDiscountField.value) {
          const { value: discountPrice } = this.discountPriceField;
          this.issuePriceLegalField.setValue(discountPrice);
        }
      })
      .add(this.discountCertificateSubscriptions);

    this.underlyingAssets.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        this.discountCertificateOrEmpty(),
        startWith(this.underlyingAssets.value)
      )
      .subscribe(() => {
        this.toggleIssueAtDiscount();
        this.toggleInitialLevelAsDenomination();
      })
      .add(this.discountCertificateSubscriptions);

    this.initialLevelAsDenomination.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.denominationLegalField['readOnly'] = value;

        if (!value || !this.underlyingAssets.value?.length) {
          return;
        }

        this.denominationLegalField.setValue(
          this.underlyingAssets.value[0].initialFixing
        );
      })
      .add(this.discountCertificateSubscriptions);

    this.issueAtDiscountField.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(this.issueAtDiscountField.value)
      )
      .subscribe((value) => {
        value &&
          this.issuePriceLegalField.setValue(this.discountPriceField.value);
        this.issuePriceLegalField['readOnly'] = value;
      })
      .add(this.discountCertificateSubscriptions);

    if (this.productType.value !== ProductTypeEnum.DISCOUNT_CERTIFICATE) {
      return;
    }

    this.base.removeValidators(capVsDiscountCertificateValidator);
    this.base.addValidators(capVsDiscountCertificateValidator);
    this.base.updateValueAndValidity();
  }

  toggleIssueAtDiscount(): void {
    const isDiscountPriceAbs = this.strikeInputRef?.unit === 'abs';

    const disableIssueAtDiscount =
      this.underlyingAssets?.value?.length > 1 && isDiscountPriceAbs;

    const isReadOnly = this.initialLevelAsDenomination['readOnly'];
    if (!disableIssueAtDiscount && isReadOnly) {
      this.issueAtDiscountField.setValue(true);
    } else if (disableIssueAtDiscount) {
      this.issueAtDiscountField.setValue(false);
    }

    this.issueAtDiscountField['readOnly'] = disableIssueAtDiscount;
  }

  toggleInitialLevelAsDenomination(): void {
    const disableInitialLevel = this.underlyingAssets?.value?.length > 1;
    const isReadOnly = this.initialLevelAsDenomination['readOnly'];

    if (!disableInitialLevel && isReadOnly) {
      this.initialLevelAsDenomination.setValue(true);
    } else if (disableInitialLevel) {
      this.initialLevelAsDenomination.setValue(false);
    }
    const isSelected = this.initialLevelAsDenomination.value;

    this.initialLevelAsDenomination['readOnly'] = disableInitialLevel;
    this.denominationLegalField['readOnly'] =
      isSelected && !disableInitialLevel;

    if (
      isSelected &&
      !disableInitialLevel &&
      this.underlyingAssets?.value?.length === 1
    ) {
      this.denominationLegalField.setValue(
        this.underlyingAssets.value[0].initialFixing
      );
    }
  }

  toggleDiscountCertificateFields(): void {
    const isDiscountCertificate =
      this.productType.value === ProductTypeEnum.DISCOUNT_CERTIFICATE;
    const toggleFn = isDiscountCertificate ? 'enable' : 'disable';
    const fields = ['issueAtDiscount', 'initialLevelAsDenomination'];

    fields.forEach((fieldName: string) => {
      const field = this.base.get(fieldName);
      field[toggleFn]();

      if (!isDiscountCertificate) {
        field.setValue(null);
      }
    });
  }

  private updateBaseLeverage(baseStrike: any): void {
    const baseType = this.base.get('type').value;
    const baseLeverage = this.base.get('leverage');

    if (baseType === 'NONE' || baseType === 'DELTA_N') {
      baseLeverage['readOnly'] = false;
      return;
    }

    // The only other cases will be where baseType is `BONUS_CERTIFICATE`
    // or `BONUS_CERTIFICATE_NO_BARRIER`, and in both cases the leverage
    // field should be read-only
    baseLeverage['readOnly'] = true;
    const strikeAsNumber =
      getFinalOrIndicativeValueFromIndicativeObject(baseStrike);
    baseLeverage.setValue(
      strikeAsNumber
        ? {
            value: (1 / (strikeAsNumber / 100)).toString(),
            indicativeValue: null,
            minValue: null,
            maxValue: null,
          }
        : {
            value: null,
            indicativeValue: null,
            minValue: null,
            maxValue: null,
          }
    );
  }

  discountCertificateOrEmpty(): OperatorFunction<any, any> {
    return switchMap(() =>
      this.productType.value === ProductTypeEnum.DISCOUNT_CERTIFICATE
        ? of(this.discountPriceField.value)
        : EMPTY
    );
  }

  private setupBonusAmountSubscription(): void {
    const bonusStrike$ = this.base.get('bonusStrike').valueChanges;
    const baseType$ = this.base.get('type').valueChanges;
    const baseStrike$ = this.base.get('strike').valueChanges;
    const baseLeverage$ = this.base.get('leverage').valueChanges;

    combineLatest([bonusStrike$, baseType$, baseStrike$, baseLeverage$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([bonusStrike, baseType, baseStrike, baseLeverage]) => {
        if (baseType === 'BONUS_CERTIFICATE') {
          const strikeAsNumber =
            getFinalOrIndicativeValueFromIndicativeObject(baseStrike);
          const leverageAsNumber =
            getFinalOrIndicativeValueFromIndicativeObject(baseLeverage);
          const bonusStrikeAsNumber =
            getFinalOrIndicativeValueFromIndicativeObject(bonusStrike);

          if (
            strikeAsNumber !== null &&
            leverageAsNumber !== null &&
            bonusStrikeAsNumber !== null
          ) {
            const bonusAmount = Math.max(
              (bonusStrikeAsNumber - strikeAsNumber) * leverageAsNumber,
              0
            );
            const bonusAmountAsString = bonusAmount + '%';
            this.base.get('bonusAmount').setValue({
              value: bonusAmountAsString,
              indicativeValue: null,
              minValue: null,
              maxValue: null,
            });
          } else {
            this.base.get('bonusAmount').setValue({
              value: null,
              indicativeValue: null,
              minValue: null,
              maxValue: null,
            });
          }

          return;
        }

        if (baseType === 'BONUS_CERTIFICATE_NO_BARRIER') {
          const bonusStrikeAsNumber =
            getFinalOrIndicativeValueFromIndicativeObject(bonusStrike);

          if (bonusStrikeAsNumber !== null) {
            const bonusAmount = Math.max(bonusStrikeAsNumber - 100, 0);
            const bonusAmountAsString = bonusAmount + '%';
            this.base.get('bonusAmount').setValue({
              value: bonusAmountAsString,
              indicativeValue: null,
              minValue: null,
              maxValue: null,
            });
          } else {
            this.base.get('bonusAmount').setValue({
              value: null,
              indicativeValue: null,
              minValue: null,
              maxValue: null,
            });
          }
        }
      });
  }

  filterDatesToRemoveNonDateObjects(
    arrayOfObjects: PayoffSchedule[],
    objProperty: keyof PayoffSchedule
  ) {
    return arrayOfObjects
      .filter(
        (obj) =>
          typeof obj[objProperty] === 'object' && obj[objProperty] !== null
      )
      .map((obj) => DateObjectToDDMMYYYstring(obj[objProperty] as NgbDate));
  }

  scheduleOutput(e): void {
    this.dateRangeOut.emit(e);
  }

  deleteBarrierAndInvestmentLevel(index: number): void {
    this.baseBarrierLevelsAndInvestments.removeAt(index);
  }

  addBarrierAndInvestmentLevel(): void {
    this.baseBarrierLevelsAndInvestments.push(
      new UntypedFormGroup({
        level: new UntypedFormControl(null),
        investmentAllocation: new UntypedFormControl(null),
      })
    );
  }

  private getDropdownOptions(): void {
    this.commonProductCaptureDataService.referenceRateOptions$.subscribe(
      (productReferenceRates) => {
        if (
          productReferenceRates?.length > 0 &&
          !this.referenceRateItems?.length
        ) {
          this.referenceRateItems = [
            {
              id: 'Please Select',
              displayValue: 'Please Select',
              disabled: true,
            },
            ...productReferenceRates.map((rate) => {
              const label = rate.assetId + ' | ' + rate.longName;

              return {
                id: label,
                displayValue: label,
              };
            }),
          ];
        }
      }
    );

    this.productCaptureService
      .getCalendarsAsDropdownOptions(CalendarType.SETTLEMENT)
      .pipe(first())
      .subscribe((calendars) => (this.settlementCalendars = calendars));
  }

  get callability(): UntypedFormGroup {
    return this.form.get(PayoffEnum.callability) as UntypedFormGroup;
  }

  get callabilityBarrier(): UntypedFormGroup {
    return this.callability.get(CallabilityEnum.barrier) as UntypedFormGroup;
  }

  get coupon(): UntypedFormGroup {
    return this.form.get(PayoffEnum.coupon) as UntypedFormGroup;
  }

  get couponBarrier(): UntypedFormGroup {
    return this.coupon.get(CouponEnum.barrier) as UntypedFormGroup;
  }

  get participation(): UntypedFormGroup {
    return this.form.get(PayoffEnum.participation) as UntypedFormGroup;
  }

  get capitalBase(): UntypedFormGroup {
    return this.form.get(PayoffEnum.capitalBase) as UntypedFormGroup;
  }

  get upside(): UntypedFormGroup {
    return this.participation.get(ParticipationEnum.upside) as UntypedFormGroup;
  }

  get upsideBarrier(): UntypedFormGroup {
    return this.upside.get(ParticipationEnum.barrier) as UntypedFormGroup;
  }

  get downside(): UntypedFormGroup {
    return this.participation.get(
      ParticipationEnum.downside
    ) as UntypedFormGroup;
  }

  get downsideBarrier(): UntypedFormGroup {
    return this.downside.get(ParticipationEnum.barrier) as UntypedFormGroup;
  }

  get downsideStrikeValue(): FormGroup {
    return this.downside.get(ParticipationEnum.strike).value as FormGroup;
  }

  get base(): FormGroup {
    return this.participation.get(ParticipationEnum.base) as FormGroup;
  }

  get baseBarrier(): UntypedFormGroup {
    return this.base.get(ParticipationEnum.barrier) as UntypedFormGroup;
  }

  get baseBarrierLevelsAndInvestments(): UntypedFormArray {
    return this.baseBarrier.get(
      ParticipationBaseBarrierEnum.levelsAndInvestments
    ) as UntypedFormArray;
  }

  get productType(): UntypedFormControl {
    return this.rootFormGroup.control
      .get(ProductCaptureGroupsEnum.summary)
      .get(SummaryEnum.productType) as UntypedFormControl;
  }

  get underlyingAssetsGroup(): UntypedFormGroup {
    return this.rootFormGroup.control
      .get(ProductCaptureGroupsEnum.summary)
      .get(SummaryEnum.underlyingAssetsGroup) as UntypedFormGroup;
  }

  get rollAs(): UntypedFormControl {
    return this.underlyingAssetsGroup.get(
      SummaryEnum.rollAs
    ) as UntypedFormControl;
  }

  get underlyingAssets(): FormArray {
    return this.underlyingAssetsGroup.get(
      SummaryEnum.underlyingAssets
    ) as FormArray;
  }

  get issueAtDiscountField(): AbstractControl {
    return this.base.get(ParticipationBaseEnum.issueAtDiscount);
  }

  get initialLevelAsDenomination(): AbstractControl {
    return this.base.get(ParticipationBaseEnum.initialLevelAsDenomination);
  }

  get denominationLegalField(): AbstractControl {
    return this.rootFormGroup.control.get('legalForm.legal.denominationLegal');
  }

  get discountPriceField(): AbstractControl {
    return this.base.get(ParticipationBaseEnum.strike);
  }

  get issuePriceLegalField(): AbstractControl {
    return this.rootFormGroup.control.get('legalForm.legal.issuePriceLegal');
  }

  get globalCap(): AbstractControl {
    return this.base.get(ParticipationBaseEnum.globalCap);
  }

  get rateObsPeriodShift(): AbstractControl {
    return this.coupon.get(CouponEnum.rateObservationPeriodShift);
  }

  get rateObsPeriodShiftExtraCalendars(): AbstractControl {
    return this.coupon.get(CouponEnum.rateObsPeriodShiftExtraCalendars);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
